import type { ColumnSort, SortingState } from "@tanstack/react-table";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  activeViewIdAtomFamily,
  clearLastFinalSchemaLocalStore,
  columnsVisibilityGridSchemaStateSF,
  companyIdSelector,
  defaultViewSF,
  fixedColumnNamesAF,
  getConstInitialFiltersByEntity,
  gridControlsSF,
  gridSchemaOrderSF,
  gridSchemaSF,
  NO_GROUPING_QS_VALUE,
  tableColumnsOrderStateAF,
  tableColumnsSizingAF,
  tableColumnsSortingAF,
  tableColumnsVisibilityAF,
  tableFiltersSchemaAtomFamily,
  tableGroupingAF,
  transformFiltersIdsToStoreObj,
  unique,
  updateFinalSchemaLocalStore,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { useCustomQueryParams } from "../navigation";
import { useDynamicFormsUtils } from "./use-dynamic-forms.utils";

export const useResetFinalSchema = () => {
  const { entity } = usePageContext();
  const companyId = useRecoilValue(companyIdSelector);

  const { resetAllQs } = useCustomQueryParams();
  const defaultView = useRecoilValue(defaultViewSF(entity));
  const { sortBy, defaultFilters, groupBy } = useRecoilValue(
    gridSchemaSF(entity),
  );
  const gridSchemaOrder = useRecoilValue(gridSchemaOrderSF(entity));
  const gridControls = useRecoilValue(gridControlsSF(entity));
  const gridSchemaVisibility = useRecoilValue(
    columnsVisibilityGridSchemaStateSF(entity),
  );
  const setTableFilters = useSetRecoilState(
    tableFiltersSchemaAtomFamily(entity),
  );
  const setColumnOrder = useSetRecoilState(tableColumnsOrderStateAF(entity));
  const setColumnSorting = useSetRecoilState(tableColumnsSortingAF(entity));
  const setColumnGrouping = useSetRecoilState(tableGroupingAF(entity));
  const setColumnVisibility = useSetRecoilState(
    tableColumnsVisibilityAF(entity),
  );
  const setColumnsSizing = useSetRecoilState(tableColumnsSizingAF(entity));
  const fixedColumnNames = useRecoilValue(fixedColumnNamesAF(entity));

  const { getGridFieldNameByControlId } = useDynamicFormsUtils();
  const setActiveView = useSetRecoilState(activeViewIdAtomFamily(entity));

  const resetFinalSchema = useEvent(() => {
    if (!companyId) {
      return;
    }

    clearLastFinalSchemaLocalStore(companyId, entity);

    resetAllQs();

    const gridSchemaOrderFallback = gridControls
      .map(control => getGridFieldNameByControlId(Number(control.id)))
      .filter(fieldName => fieldName && !fieldName.startsWith("unresolved_"));

    const columnOrder =
      defaultView?.viewState?.columnsOrder ??
      (gridSchemaOrder.length ? gridSchemaOrder : gridSchemaOrderFallback);
    const columnOrderResolved = unique([
      ...fixedColumnNames.fixedColumns,
      ...columnOrder,
      ...fixedColumnNames.lastColumns,
    ]);

    const resolveGridSchemaSorting = (
      sorting: ColumnSort[] | undefined,
    ): SortingState =>
      sorting
        ?.map(columnSort => {
          const id = getGridFieldNameByControlId(Number(columnSort.id));
          return id && !id.startsWith("unresolved_")
            ? { id, desc: columnSort.desc ?? false }
            : null;
        })
        .filter((item): item is ColumnSort => item !== null) ?? [];

    const columnSorting =
      defaultView?.viewState?.columnsSorting ??
      resolveGridSchemaSorting(sortBy);
    const gridSchemaGrouping = groupBy?.length
      ? [getGridFieldNameByControlId(Number(groupBy?.[0].id))].filter(x => x)
      : [];
    const gridSchemaGroupingResolved = gridSchemaGrouping?.length
      ? gridSchemaGrouping
      : [NO_GROUPING_QS_VALUE];
    const viewGrouping = defaultView?.viewState?.columnsGrouping;
    const columnGrouping = viewGrouping?.length
      ? viewGrouping
      : gridSchemaGroupingResolved;
    const columnVisibility =
      defaultView?.viewState?.columnsVisibility ?? gridSchemaVisibility ?? {};
    // No widths puts the grid back to stretching its default widths over the screen.
    const columnsSizing = defaultView?.viewState?.columnsSizing ?? {};
    const filtersSchemaComputed =
      defaultView?.viewState?.filtersSchema ??
      transformFiltersIdsToStoreObj(defaultFilters) ??
      getConstInitialFiltersByEntity(entity);

    setColumnOrder(columnOrderResolved);
    setColumnSorting(columnSorting);
    setColumnGrouping(columnGrouping);
    setColumnVisibility(columnVisibility);
    setColumnsSizing(columnsSizing);

    if (defaultView?.id) {
      setActiveView(defaultView.id);
    }

    setTableFilters(filtersSchemaComputed);

    updateFinalSchemaLocalStore({
      companyId,
      page: entity,
      finalSchema: {
        columnsSorting: columnSorting,
        columnsVisibility: columnVisibility,
        columnsGrouping: columnGrouping,
        columnsOrder: columnOrderResolved,
        columnsSizing,
        filtersSchema: defaultView?.viewState?.filtersSchema,
        filtersCriteria: defaultView?.viewState?.filtersCriteria,
      },
    });
  });

  return {
    resetFinalSchema,
  };
};
